import { useState } from 'react';
import { useFund } from '../context/FundContext';
import { api } from '../api';

const REPORTING_TYPES = ['Generic'];
const REPORTING_LEVELS = ['Fund'];

// First day of the month for a YYYY-MM-DD string.
function monthStart(dateStr) {
  return dateStr ? `${dateStr.slice(0, 7)}-01` : '';
}

// NAV Pack export — mirrors the Seamless "Reporting Package" screen. Formidium's
// own getReport endpoint is undocumented and rejects every request, so the pack
// is assembled server-side from the data endpoints that work (balance sheet,
// performance, holdings) and downloaded as a multi-sheet Excel workbook.
export default function NavPack() {
  const { funds, fund, setFund, date } = useFund();
  const [reportingType, setReportingType] = useState('Generic');
  const [reportingLevel, setReportingLevel] = useState('Fund');
  const [startDate, setStartDate] = useState(monthStart(date));
  const [endDate, setEndDate] = useState(date || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const exportPack = async () => {
    setError(null);
    setDone(null);
    if (!fund || !startDate || !endDate) {
      setError('Select a fund and a start/end date.');
      return;
    }
    if (startDate > endDate) {
      setError('Start date must be on or before end date.');
      return;
    }
    setBusy(true);
    try {
      const { blob, filename } = await api.navPack({
        fund, startDate, endDate, reportingType, reportingLevel,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDone(`Downloaded ${filename}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h2 className="page-title">NAV Pack</h2>
      <div className="panel">
        <div className="navpack-grid">
          <div className="field">
            <label>Reporting Type</label>
            <select value={reportingType} onChange={(e) => setReportingType(e.target.value)}>
              {REPORTING_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="field">
            <label>End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="field">
            <label>Reporting Level</label>
            <select value={reportingLevel} onChange={(e) => setReportingLevel(e.target.value)}>
              {REPORTING_LEVELS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Fund</label>
            <select value={fund} onChange={(e) => setFund(e.target.value)}>
              {funds.map((f) => (
                <option key={f.id} value={f.name}>{f.alias || f.name}</option>
              ))}
            </select>
          </div>
          <div className="field field-btn">
            <button className="btn" onClick={exportPack} disabled={busy}>
              {busy ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error">Error: {error}</div>}
      {done && <div className="notice">{done}</div>}

      <div className="panel">
        <p className="muted">
          The NAV Pack is assembled from live Formidium data — balance sheet, daily
          performance and holdings — for the selected fund and period, and downloads as a
          multi-sheet Excel workbook (Summary, Balance Sheet, Performance, Holdings).
        </p>
      </div>
    </div>
  );
}
